import React from 'react';
import {
  Card,
  Pagination,
  PageSection,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Label,
  PaginationVariant
} from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { environmentFindingsRows, environmentFindingsColumns, highPriorityThresh, lowPriorityThresh } from '@app/Findings/sampleData';
import { ChartDonut } from '@patternfly/react-charts';
import { PRODUCT_NAME } from '../../config/partner';

export const environmentFindings: React.FunctionComponent = () => {
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [paginatedRows, setPaginatedRows] = React.useState(environmentFindingsRows.slice(0, 10));

  const handleSetPage = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number,
    _perPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(environmentFindingsRows.slice(startIdx, endIdx));
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(environmentFindingsRows.slice(startIdx, endIdx));
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={environmentFindingsRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const tableToolbar = (
    <Toolbar usePageInsets id="compact-toolbar">
      <ToolbarContent>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'Running':
        return <Label color="green">{labelText}</Label>;
      case 'Stopped':
        return <Label color="orange">{labelText}</Label>;
      case 'Needs Maintenance':
        return <Label color="blue">{labelText}</Label>;
      case 'Down':
        return <Label color="red">{labelText}</Label>;
      default:
        return <></>;
    }
  };

  const renderPriority = (priority: number) => {
    if (priority > highPriorityThresh) {
        return <Label color="red">{priority}</Label>;
    } else if (priority >= lowPriorityThresh) {
        return <Label color="orange">{priority}</Label>;
    } else {
        return <Label color="green">{priority}</Label>;
    }
  };

  const highCount = environmentFindingsRows.filter((obj) => obj.Priority > highPriorityThresh).length;
  const mediumCount = environmentFindingsRows.filter((obj) => obj.Priority <= highPriorityThresh).filter((obj) => obj.Priority >= lowPriorityThresh).length;
  const lowCount = environmentFindingsRows.filter((obj) => obj.Priority < lowPriorityThresh).length;

  return (
    <React.Fragment>
        <PageSection isFilled>
          <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Environment Findings</Title>
          <br />
          <Card>
            {tableToolbar}
            <Table variant="compact" aria-label="Compact Table">
              <Thead>
                <Tr>
                  <Th key={0}>{environmentFindingsColumns[0]}</Th>
                  <Th key={1}>{environmentFindingsColumns[1]}</Th>
                  <Th key={2}>{environmentFindingsColumns[2]}</Th>
                  <Th key={3}>{environmentFindingsColumns[3]}</Th>
                  <Th key={4}>{environmentFindingsColumns[4]}</Th>
                  <Th key={5}>{environmentFindingsColumns[5]}</Th>
                  <Th key={6}>{environmentFindingsColumns[6]}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <>
                      <Td dataLabel={environmentFindingsColumns[0]}>{row.Source}</Td>
                      <Td dataLabel={environmentFindingsColumns[1]}>{renderPriority(row.Priority)}</Td>
                      <Td dataLabel={environmentFindingsColumns[2]}>{row.FindingName}</Td>
                      <Td dataLabel={environmentFindingsColumns[3]}>{row.Resource}</Td>
                      <Td dataLabel={environmentFindingsColumns[4]}>{row.Description}</Td>
                      <Td dataLabel={environmentFindingsColumns[5]}>{row.Remediation}</Td>
                      <Td dataLabel={environmentFindingsColumns[6]}>{row.MoreInfo}</Td>
                    </>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
        </PageSection>

        <div style={{ height: '230px', width: '450px' }}>
          <ChartDonut
            ariaDesc="Environment findings by priority"
            ariaTitle="Environment findings by priority"
            data={[{ x: 'High', y: highCount }, { x: 'Medium', y: mediumCount }, { x: 'Low', y: lowCount }]}
            labels={({ datum }) => `${datum.x}: ${datum.y}`}
            style={{
                data: {
                    fill: ({ datum }) => {
                        if (datum.x === 'High') {
                            return 'red';
                        } else if (datum.x === 'Medium') {
                            return 'orange';
                        } else if (datum.x === 'Low') {
                            return 'green';
                        }
                        return null;
                    }
                }
            }}
            legendData={[{ name: 'High Priority: '+highCount, symbol: { fill: 'red' }}, { name: 'Medium Priority: '+mediumCount, symbol: { fill: 'orange' }}, { name: 'Low Priority: '+lowCount, symbol: { fill: 'green' }}]}
            legendOrientation="vertical"
            legendPosition="right"
            name="chart1"
             padding={{
               bottom: 20,
               left: 50,
               right: 200, // Adjusted to accommodate legend
               top: 20
             }}
            title={environmentFindingsRows.length}
            subTitle="Environment Findings"
            width={450}
            innerRadius={80}
            constrainToVisibleArea
          />
        </div>
    </React.Fragment>
  );
};
